import { Link } from "react-router-dom";
import { PageHeader, CTASection, Disclaimer } from "@/components/Shared";
import { Award, CheckCircle2, MapPin, Flag, Vote, Plane, Users, AlertCircle, ExternalLink, ArrowRight } from "lucide-react";

const SECTIONS = [
  {
    icon: Award,
    title: "What Is Australian Citizenship?",
    body: [
      "Australian citizenship may be conferred upon eligible permanent residents of Australia who have lived in Australia for the required period and satisfy all legislative requirements. Australian citizens enjoy the same rights, privileges and responsibilities as other Australian citizens.",
    ],
  },
  {
    icon: Plane,
    title: "Benefits of Australian Citizenship",
    list: [
      "Apply for an Australian passport",
      "Leave and re-enter Australia freely",
      "Seek assistance from Australian consulates overseas",
      "Vote in federal, state and territory elections",
      "Vote in constitutional referendums",
      "Apply for certain Australian government and defence roles",
      "Register eligible children born overseas as Australian citizens",
    ],
  },
  {
    icon: Vote,
    title: "Responsibilities of Australian Citizens",
    list: [
      "Respect Australia's democratic beliefs and laws",
      "Follow and obey Australian laws",
      "Vote in elections and referendums",
      "Serve on jury duty if required",
      "Defend Australia if necessary",
    ],
  },
  {
    icon: Flag,
    title: "Ways to Become an Australian Citizen",
    body: ["There are several pathways to Australian citizenship, including:"],
    list: [
      "Citizenship by birth",
      "Citizenship by descent",
      "Citizenship by conferral (for eligible permanent residents)",
    ],
  },
];

const ELIGIBILITY = [
  "Be a permanent resident of Australia",
  "Have held permanent residency for at least the last 12 months",
  "Have lawfully lived in Australia for at least 4 years",
  "Not have spent excessive time outside Australia during the qualifying period",
  "Be of good character if aged 18 years or older",
  "Intend to maintain a close and continuing connection with Australia",
];

const RESIDENCE = [
  "Have lived lawfully in Australia for the last 4 years",
  "Not have been absent from Australia for more than 12 months in total during the last 4 years",
  "Not have spent more than 90 days outside Australia during the last 12 months before applying",
];

const REFUSAL_REASONS = [
  "Identity cannot be established",
  "Residence requirements are not met",
  "Character concerns arise",
  "Significant criminal matters exist",
  "Supporting evidence is insufficient",
  "Legislative requirements are not satisfied",
];

const HOW_WE_ASSIST = [
  "Citizenship eligibility assessments",
  "Residence requirement calculations",
  "Supporting document guidance",
  "Citizenship application preparation",
  "Travel history considerations",
  "General citizenship process support",
  "Guidance for eligible New Zealand citizens",
];

export default function CitizenshipApplication() {
  return (
    <div>
      <PageHeader
        eyebrow="Subclass · Conferral · Descent · Citizenship Test"
        title="Your Journey to Becoming an Australian Citizen."
        subtitle="Australian citizenship represents belonging, opportunity, security and a long-term commitment to Australia and its values. We guide eligible applicants through the process with professionalism, care and clarity."
        image="/assets/sydney-harbour-sunset.jpg"
      />

      {/* Intro */}
      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-slate-600 leading-relaxed text-[15px] space-y-5">
          <p>
            For many migrants and families, becoming an Australian citizen is one of the most meaningful milestones in their journey. It is the moment a long pathway of study, work, sacrifice and contribution becomes a permanent home.
          </p>
          <p>
            We understand the emotional and practical importance of citizenship applications. Our team supports eligible permanent residents through eligibility checks, residence calculations, document preparation and lodgement — and continues to support you up to your ceremony.
          </p>
        </div>
      </section>

      {/* Card sections */}
      <section className="py-12 md:py-16 bg-[#F8F9FA]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-px bg-slate-200">
          {SECTIONS.map((s, i) => (
            <div key={i} className="bg-white p-7">
              <s.icon className="h-7 w-7 text-[#C43A89] mb-4" />
              <h3 className="font-display text-xl text-[#29324A] mb-3">{s.title}</h3>
              {s.body?.map((p, pi) => <p key={pi} className="text-sm text-slate-600 leading-relaxed mb-2">{p}</p>)}
              {s.list && (
                <ul className="mt-3 space-y-2 text-sm text-slate-600">
                  {s.list.map((item, li) => (
                    <li key={li} className="flex items-start gap-2.5">
                      <CheckCircle2 className="h-4 w-4 text-[#29324A] mt-0.5 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Citizenship by conferral */}
      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12">
          <div>
            <div className="text-[11px] font-bold tracking-[0.22em] text-[#C43A89] uppercase mb-3 flex items-center gap-3">
              <span className="brand-dot bg-[#C43A89]" /> Citizenship by Conferral
            </div>
            <h2 className="font-display text-3xl md:text-4xl text-[#29324A] leading-tight">
              The most common pathway for migrants who have become permanent residents.
            </h2>
            <p className="mt-5 text-sm text-slate-600 leading-relaxed">
              Citizenship by conferral is one of the most common pathways for migrants who have become permanent residents of Australia. In most cases, applicants must meet the eligibility, residence, character and intention requirements set out below.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <h4 className="font-display text-lg text-[#29324A] mb-3">Eligibility</h4>
              <ul className="space-y-2 text-sm text-slate-600">
                {ELIGIBILITY.map((e, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <CheckCircle2 className="h-4 w-4 text-[#C43A89] mt-0.5 shrink-0" />
                    <span>{e}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-display text-lg text-[#29324A] mb-3">Residence Requirements</h4>
              <ul className="space-y-2 text-sm text-slate-600">
                {RESIDENCE.map((e, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <MapPin className="h-4 w-4 text-[#29324A] mt-0.5 shrink-0" />
                    <span>{e}</span>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-slate-500 mt-3 italic">
                Travel history can be important. The Department may require details of overseas travel since you turned 18.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Test + Ceremony */}
      <section className="py-14 md:py-20 bg-[#F8F4FB]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-8">
          <div className="bg-white p-7 border-l-4 border-[#29324A]">
            <div className="text-[10px] font-bold tracking-[0.22em] text-[#29324A] uppercase mb-3">Citizenship Test</div>
            <h3 className="font-display text-2xl text-[#29324A] leading-tight mb-3">Knowing Australia</h3>
            <p className="text-sm text-slate-600 leading-relaxed mb-4">
              Eligible applicants are generally required to attend a citizenship appointment and complete the Australian Citizenship Test. The test includes questions on:
            </p>
            <ul className="space-y-2 text-sm text-slate-600">
              <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-[#29324A] mt-0.5 shrink-0" /> Australia's values and democratic beliefs</li>
              <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-[#29324A] mt-0.5 shrink-0" /> Rights and responsibilities of Australian citizens</li>
              <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-[#29324A] mt-0.5 shrink-0" /> Australian society and culture</li>
            </ul>
            <p className="text-xs text-slate-500 mt-4 italic">Applicants aged 60 years or older are generally not required to sit the test.</p>
          </div>
          <div className="bg-white p-7 border-l-4 border-[#C43A89]">
            <div className="text-[10px] font-bold tracking-[0.22em] text-[#C43A89] uppercase mb-3">Citizenship Ceremony</div>
            <h3 className="font-display text-2xl text-[#29324A] leading-tight mb-3">Becoming Australian</h3>
            <p className="text-sm text-slate-600 leading-relaxed">
              After approval, applicants are usually required to attend an Australian Citizenship Ceremony and make the Australian Citizenship Pledge before officially becoming an Australian citizen. Ceremonies are generally organised through local councils across Australia.
            </p>
          </div>
        </div>
      </section>

      {/* Govt charge + refusals + NZ + StudyNet support */}
      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-8">
          <div>
            <h3 className="font-display text-2xl text-[#29324A] mb-3">Current Government Application Charge</h3>
            <p className="text-sm text-slate-600 leading-relaxed">
              The current application fee for Australian Citizenship by Conferral is <strong className="text-[#29324A]">AUD 575</strong> unless a concession applies. Government charges may change over time.
            </p>
            <a
              href="https://immi.homeaffairs.gov.au/"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center gap-2 text-xs font-bold tracking-wider uppercase text-[#29324A] hover:text-[#C43A89] transition-colors border-b border-[#29324A] pb-0.5"
            >
              Latest fees on immi.homeaffairs.gov.au <ExternalLink className="h-3.5 w-3.5" />
            </a>

            <h3 className="font-display text-2xl text-[#29324A] mt-10 mb-3 flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-[#C43A89]" /> Common reasons applications may be refused
            </h3>
            <ul className="space-y-2 text-sm text-slate-600">
              {REFUSAL_REASONS.map((r, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <span className="brand-dot bg-[#C43A89] mt-2" />
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-[#F8F9FA] p-7 border-l-4 border-[#29324A]">
            <Users className="h-7 w-7 text-[#29324A] mb-3" />
            <h3 className="font-display text-2xl text-[#29324A] mb-3">Special information for New Zealand citizens</h3>
            <p className="text-sm text-slate-600 leading-relaxed">
              Certain New Zealand citizens holding Special Category Visas (SCV) may have direct pathways to Australian citizenship depending on their residency history and visa status. Additional eligibility provisions may apply from legislative changes introduced from 1 July 2023.
            </p>
            <h4 className="font-display text-lg text-[#29324A] mt-7 mb-3">How we can assist</h4>
            <ul className="space-y-2 text-sm text-slate-600">
              {HOW_WE_ASSIST.map((r, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <CheckCircle2 className="h-4 w-4 text-[#C43A89] mt-0.5 shrink-0" />
                  <span>{r}</span>
                </li>
              ))}
            </ul>
            <Link
              to="/book-consultation"
              data-testid="citizenship-book-cta"
              className="mt-7 inline-flex items-center gap-2 bg-[#C43A89] hover:bg-[#a22b6e] text-white font-semibold py-3 px-6 transition-colors btn-shimmer text-xs uppercase tracking-wider"
            >
              Book a Citizenship Consultation <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="bg-white py-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Disclaimer>
            Information on this page is general in nature and does not replace personalised migration advice. Citizenship law and Department charges may change. Speak with one of our Registered Migration Agents for a tailored eligibility assessment.
          </Disclaimer>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
